import React, { FC } from 'react';
import { useTracking } from 'react-tracking';
import { TrackEventInput } from '@updater/ui-tracker';
import { BadgeConfig, CardData, IconName } from 'flows/core/types/cards';
import {
  CardV2,
  CardFooter,
  CardFooterRight,
  Box,
  H1,
  ButtonV2,
  Text,
  Badge,
  Flex,
} from '@updater/ui-uds';
import {
  TabLayoutGrid,
  TabLayoutGridProps,
} from 'flows/core/components/tab-layout-grid';

import { Tab } from 'components/nav-bar/NavBar';
import { SingleCard } from 'flows/core/components/cards/single-card';
import { getIconComponent } from 'utils/get-icon';

/* Properties */
interface TabLayoutCardV2LayoutProps extends TabLayoutGridProps {
  data: CardData[];
  currentTab?: Tab;
  section?: string;
}

interface NewCardProps {
  identifier?: string;
  icon: IconName;
  badge?: BadgeConfig;
  heading: string;
  description: string;
  buttonText: string;
  onButtonClick?: () => void;
}

/* Composed components */
export const NewCardComponent: FC<NewCardProps> = ({
  identifier,
  icon,
  badge,
  heading,
  description,
  buttonText,
  onButtonClick,
}) => {
  return (
    <CardV2 variant="rounded" data-testid={`cardV2-${identifier}`}>
      <Box padding="m">
        <Flex alignItems="center">
          {getIconComponent(icon)}
          {badge && (
            <Badge data-testid="badge" width="fit-content" ml="xs" {...badge} />
          )}
        </Flex>
        <H1 mt="xs" variant="l" data-testid="cardV2-heading">
          {heading}
        </H1>
        <Text variant="s" data-testid="cardV2-description">
          {description}
        </Text>
      </Box>
      <CardFooter variant="box">
        <CardFooterRight>
          <ButtonV2
            data-testid={`footer-button-${identifier}`}
            onClick={onButtonClick}
            variant="primary"
            size="s"
          >
            {buttonText}
          </ButtonV2>
        </CardFooterRight>
      </CardFooter>
    </CardV2>
  );
};

/* Rendered components */
export const TabLayoutCardV2Layout: React.FC<TabLayoutCardV2LayoutProps> = ({
  title,
  gridTemplateColumns,
  data,
  currentTab,
  gridGap,
}) => {
  const { Track } = useTracking<TrackEventInput<unknown>>();

  // If data is buggy, show error message
  if (!Array.isArray(data)) {
    return <div>Incorrect data!</div>;
  }

  // Sets anything discarded (discard type card) in essentials
  data.map((card) => {
    const cardCopy = { ...card };

    if ('showDiscard' in cardCopy.data) {
      cardCopy.data.showDiscard = currentTab === 'essentials';
    }

    return cardCopy;
  });

  return (
    <Track>
      <TabLayoutGrid
        data-testid="experiment-tablayout-grid"
        title={title}
        gridTemplateColumns={gridTemplateColumns}
        gridGap={gridGap}
      >
        {data.map((card, index) => (
          <SingleCard
            data-testid={`card-${card.data.subtype}-control`}
            card={card}
            position={index}
            key={card.identifier}
          />
        ))}
      </TabLayoutGrid>
    </Track>
  );
};
